import React from 'react';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import { WebUiButton } from '../../components/button';
import WebUiPageWrapper from '../../components/page-wrapper';
import { WebUiSelect } from '../../components/select';
import { useWebUiGlobalContext } from '../../context/global/use-global-context';
import { QosDscpPcpMatrix, QosDscpQueueMatrix } from './qos/qos-dscp-matrix';

type PortDefault = {
  interface: number;
  'prio choice':
    | 'default'
    | 'pcp > default'
    | 'dscp > default'
    | 'pcp > dscp > default';
  queue: number;
  pcp: number;
};
type PortInfo = { interface: number; name?: string };

const PRIO_OPTIONS: PortDefault['prio choice'][] = [
  'default',
  'pcp > default',
  'dscp > default',
  'pcp > dscp > default',
];

function queueLabel(queue: number): string {
  const labels = [
    'Q0 (weighted, 1x)',
    'Q1 (weighted, 2x)',
    'Q2 (weighted, 3x)',
    'Q3 (weighted, 6x)',
    'Q4 (weighted, 12x)',
    'Q5 (3rd strict)',
    'Q6 (2nd strict)',
    'Q7 (1st strict)',
  ];
  return labels[queue] ?? `Q${queue}`;
}

function normalizeArray(data: unknown, size: number): number[] {
  if (Array.isArray(data)) {
    return Array.from({ length: size }, (_, i) => Number(data[i] ?? 0));
  }
  const obj = (data ?? {}) as Record<string, unknown>;
  return Array.from({ length: size }, (_, i) => Number(obj[i] ?? 0));
}

export default function WebUiSettingsQosPage() {
  const { currentUser } = useWebUiGlobalContext();
  const readonly = currentUser?.userLevel === 1;
  const [ports, setPorts] = React.useState<PortInfo[]>([]);
  const [defaults, setDefaults] = React.useState<PortDefault[]>([]);
  const [pcp, setPcp] = React.useState<number[]>(
    Array.from({ length: 8 }, () => 0),
  );
  const [dscpQueue, setDscpQueue] = React.useState<number[]>(
    Array.from({ length: 64 }, () => 0),
  );
  const [dscpPcp, setDscpPcp] = React.useState<number[]>(
    Array.from({ length: 64 }, () => 0),
  );
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    const [ifs, d0, d1, d2, d3] = await Promise.all([
      postWebUiApi<PortInfo[]>({
        service: 'interface',
        access: 'get',
        command: 'get interfaces',
      }),
      postWebUiApi({ service: 'qos', access: 'get', command: 'get defaults' }),
      postWebUiApi({
        service: 'qos',
        access: 'get',
        command: 'get pcp->queue mapping',
      }),
      postWebUiApi({
        service: 'qos',
        access: 'get',
        command: 'get dscp->queue mapping',
      }),
      postWebUiApi({
        service: 'qos',
        access: 'get',
        command: 'get dscp->pcp mapping',
      }),
    ]);
    const portList = (ifs ?? [])
      .slice()
      .sort((a, b) => a.interface - b.interface);
    setPorts(portList);
    const incomingDefaults = Array.isArray(d0) ? (d0 as PortDefault[]) : [];
    const byIf = new Map(
      incomingDefaults.map((item) => [item.interface, item]),
    );
    setDefaults(
      portList.map((p) => {
        const found = byIf.get(p.interface);
        return {
          interface: p.interface,
          'prio choice': found?.['prio choice'] ?? 'default',
          queue: Number(found?.queue ?? 0),
          pcp: Number(found?.pcp ?? 0),
        };
      }),
    );
    setPcp(normalizeArray(d1, 8));
    setDscpQueue(normalizeArray(d2, 64));
    setDscpPcp(normalizeArray(d3, 64));
  }, []);

  React.useEffect(() => {
    reload().catch((err) => setError((err as Error).message));
  }, [reload]);

  const save = async (command: string, parameters: unknown, ok: string) => {
    try {
      await postWebUiApi({
        service: 'qos',
        access: 'set',
        command,
        parameters,
      });
      setMessage(ok);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <WebUiPageWrapper titleList={['Settings', 'QoS']}>
      {message ? <WebUiAlert type="success" message={message} /> : null}
      {error ? <WebUiAlert type="error" message={error} /> : null}
      <div className="flex w-full flex-col gap-2">
        <h4 className="font-bold">QoS Port Settings</h4>
        <table className="webui-table max-w-fit">
          <thead>
            <tr className="webui-table-row-head">
              <th>Port</th>
              <th>Priority Criterion</th>
              <th>Default Queue</th>
              <th>Default PCP</th>
            </tr>
          </thead>
          <tbody>
            {defaults.map((d) => (
              <tr key={d.interface} className="webui-table-row-content">
                <td className="text-center">
                  {ports.find((p) => p.interface === d.interface)?.name ??
                    d.interface}
                </td>
                <td className="text-center">
                  <WebUiSelect
                    inputSize="sm"
                    className="w-full"
                    options={PRIO_OPTIONS.map((opt) => ({
                      label: opt,
                      value: opt,
                    }))}
                    disabled={readonly}
                    value={d['prio choice'] ?? 'default'}
                    onChange={(e) =>
                      setDefaults((prev) =>
                        prev.map((x) =>
                          x.interface === d.interface
                            ? {
                                ...x,
                                'prio choice': e.target
                                  .value as PortDefault['prio choice'],
                              }
                            : x,
                        ),
                      )
                    }
                  />
                </td>
                <td>
                  <WebUiSelect
                    inputSize="sm"
                    className="w-full"
                    options={Array.from({ length: 8 }, (_, i) => ({
                      label: queueLabel(i),
                      value: i.toString(),
                    }))}
                    disabled={readonly}
                    value={d.queue.toString()}
                    onChange={(e) =>
                      setDefaults((prev) =>
                        prev.map((x) =>
                          x.interface === d.interface
                            ? { ...x, queue: Number(e.target.value) }
                            : x,
                        ),
                      )
                    }
                  />
                </td>
                <td>
                  <WebUiSelect
                    inputSize="sm"
                    className="w-full"
                    options={Array.from({ length: 8 }, (_, i) => ({
                      label: i.toString(),
                      value: i.toString(),
                    }))}
                    disabled={readonly}
                    value={d.pcp.toString()}
                    onChange={(e) =>
                      setDefaults((prev) =>
                        prev.map((x) =>
                          x.interface === d.interface
                            ? { ...x, pcp: Number(e.target.value) }
                            : x,
                        ),
                      )
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div>
          <WebUiButton
            disabled={readonly}
            onClick={() =>
              save('set defaults', defaults, 'QoS port settings updated.')
            }
          >
            Set QoS Port Settings
          </WebUiButton>
        </div>
      </div>
      <div className="mt-4 flex w-full flex-col gap-2">
        <h4 className="font-bold">PCP -&gt; Queue Mapping</h4>
        <table className="webui-table max-w-fit">
          <thead>
            <tr className="webui-table-row-head">
              <th>PCP Value</th>
              <th>Egress Queue</th>
            </tr>
          </thead>
          <tbody>
            {pcp.map((value, idx) => (
              <tr key={idx} className="webui-table-row-content">
                <td className="text-center">{idx}</td>
                <td className="text-center">
                  <WebUiSelect
                    inputSize="sm"
                    className="w-full"
                    options={Array.from({ length: 8 }, (_, i) => ({
                      label: queueLabel(i),
                      value: i.toString(),
                    }))}
                    disabled={readonly}
                    value={value.toString()}
                    onChange={(e) =>
                      setPcp((prev) =>
                        prev.map((item, itemIdx) =>
                          itemIdx === idx ? Number(e.target.value) : item,
                        ),
                      )
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div>
          <WebUiButton
            disabled={readonly}
            onClick={() =>
              save('set pcp->queue mapping', pcp, 'PCP mapping updated.')
            }
          >
            Set PCP -&gt; Queue Mapping
          </WebUiButton>
        </div>
      </div>
      <div className="mt-4 flex w-full flex-col gap-2">
        <QosDscpQueueMatrix
          values={dscpQueue}
          readonly={readonly}
          onChange={(idx, value) =>
            setDscpQueue((prev) =>
              prev.map((item, itemIdx) => (itemIdx === idx ? value : item)),
            )
          }
        />
        <div>
          <WebUiButton
            disabled={readonly}
            onClick={() =>
              save(
                'set dscp->queue mapping',
                dscpQueue,
                'DSCP mapping updated.',
              )
            }
          >
            Set DSCP Mapping
          </WebUiButton>
        </div>
      </div>
      <div className="mt-4 flex w-full flex-col gap-2">
        <QosDscpPcpMatrix
          values={dscpPcp}
          readonly={readonly}
          onChange={(idx, value) =>
            setDscpPcp((prev) =>
              prev.map((item, itemIdx) => (itemIdx === idx ? value : item)),
            )
          }
        />
        <div className="flex w-full flex-row gap-2">
          <WebUiButton
            disabled={readonly}
            onClick={() =>
              save(
                'set dscp->pcp mapping',
                dscpPcp,
                'DSCP to PCP mapping updated.',
              )
            }
          >
            Set DSCP -&gt; PCP Mapping
          </WebUiButton>
          <WebUiButton
            onClick={() =>
              reload().catch((err) => setError((err as Error).message))
            }
          >
            Reload
          </WebUiButton>
        </div>
      </div>
    </WebUiPageWrapper>
  );
}
